import React, { useCallback, type ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { GRAY_700, GRAY_900, RhetorikFontFamily } from 'constants/styling/theme'
import { Path } from 'constants/router'
import { AutosaveIndicator } from '../steps/catalogue/AutosaveIndicator.component'
import { usePfStore } from '../store'
import { PfRevampEventName, usePfRevampAnalytics } from '../analytics'
import { MUIIconButton } from 'components/common/MUIIconButton'

interface Props {
  /**
   * Optional override — main title rendered on the left. Defaults to the
   * shared "Your order" translation used across all PF revamp steps (Figma
   * top bar convention).
   */
  title?: string
  /**
   * Optional subtitle rendered below the title in smaller text.
   * Used on the Summary step to provide an orientation cue
   * ("Review your order before confirmation").
   */
  subtitle?: string
  /**
   * Optional centre slot rendered between the title and the right-hand
   * actions. Used by the inline stepper variant to host the progress stepper
   * in the top bar's middle (see `usePfStepperVariant`). Stretches to
   * fill the available width and centres its content.
   */
  center?: ReactNode
}

/**
 * Shared top bar for PF revamp step pages (Figma 2725:9156, 2742:122125).
 *
 * Left: page title ("Your order" by default).
 * Right: autosave indicator ("✓ Saved" / "Saving…") + close X that returns
 *        the user to the dashboard (draft keeps autosaving in the
 *        background).
 *
 * Not rendered inside the Propstack embed, as in legacy: `StepShell` shows only
 * the stepper there.
 */
export const PfTopBar: React.FC<Props> = ({ title, subtitle, center }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const navigate = useNavigate()

  const currentStep = usePfStore((s) => s.currentStep)
  const trackAnalytics = usePfRevampAnalytics()
  const handleClose = useCallback(() => {
    trackAnalytics({
      type: PfRevampEventName.FLOW_CLOSED,
      payload: { fromStep: currentStep },
    })
    navigate(Path.INDEX)
  }, [navigate, trackAnalytics, currentStep])

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      data-cy="pf-revamp-topbar"
      sx={{ width: '100%' }}
    >

      <Stack gap={0} sx={{ flexShrink: 0 }}>

        <Typography
          variant="text-2xl"
          fontWeight={400}
          color={GRAY_900}
          fontFamily={RhetorikFontFamily}
        >
          {title ?? t('purchase_flow_revamp:catalog.page_title')}
        </Typography>

        {subtitle &&
          <Typography variant="text-sm" color={GRAY_700}>
            {subtitle}
          </Typography>
        }

      </Stack>

      {/* Centre slot — hosts the inline progress stepper variant. When absent
          this empty flex box simply pushes title and actions to the edges
          (same as the original space-between layout). */}
      {center &&
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', px: 2.4 }}>
          {center}
        </Box>
      }

      <Stack direction="row" alignItems="center" gap={1.6} sx={{ flexShrink: 0 }}>

        <AutosaveIndicator />

        <MUIIconButton icon={<CloseRoundedIcon />} ariaLabel={t('purchase_flow_revamp:catalog.close_aria')} onClick={handleClose} data-cy="pf-revamp-topbar-close" />

      </Stack>

    </Stack>
  )
}
